import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Paper,
  Avatar,
  Typography,
  TextField,
  IconButton,
  AppBar,
  Toolbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
} from '@mui/material'
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  HelpOutline as HelpOutlineIcon,
  LockOpen as LockOpenIcon,
  Lock as LockIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { ChatMessage, ChatPartner } from '@/types/chat'
import { Profile } from '@/types/profile'
import { setPublicState, removePublicState, getPublicState } from '@/services/publicStateService'
import { getProfile, ProfileDetailResponse } from '@/services/profileService'
import { ProfileDetailView } from '@/components/ProfileDetailView'
import { useAuth } from '@/contexts/AuthContext'
import { getOrCreateChat, getChatMessages, sendChatMessage, markChatAsRead } from '@/services/chatService'
import {
  subscribeNewChatMessages,
  sendChatMessageRealtime,
  ensureChatDocument,
  notifyPartnerOfNewMessage,
  clearChatNotification,
} from '@/services/chatRealtimeService'
import { toRoomType } from '@/utils/roomType'
import { resolveOwnerName } from '@/utils/roomOwner'
import { useToast } from '@/contexts/ToastContext'

export const ChatPage = () => {
  const { chatId: profileId } = useParams<{ chatId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { showError } = useToast()
  const currentUserId = user?.uid ?? ''
  const currentDbUserId = user?.userId ?? ''
  const roomId = searchParams.get('roomId') ?? ''
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [partner, setPartner] = useState<ChatPartner | null>(null)
  // 相手情報の取得が「読み込み中」なのか「失敗」なのかを区別する。
  // 区別しないと、取得に失敗したときに読み込み表示のまま止まる。
  const [partnerError, setPartnerError] = useState<string | null>(null)
  const [activeChatId, setActiveChatId] = useState<string | null>(null)
  const [partnerFirebaseUid, setPartnerFirebaseUid] = useState<string | null>(null)
  const [dbLoadedAt, setDbLoadedAt] = useState<Date | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [isDisclosed, setIsDisclosed] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [pendingToggle, setPendingToggle] = useState<boolean>(false)
  const [isInitializing, setIsInitializing] = useState(true)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [profileDetailOpen, setProfileDetailOpen] = useState(false)
  const [profileDetail, setProfileDetail] = useState<Profile | null>(null)
  const [isLoadingProfile, setIsLoadingProfile] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [isTogglingPublic, setIsTogglingPublic] = useState(false)

  useEffect(() => {
    if (!profileId) return

    const fetchData = async () => {
      setPartnerError(null)
      try {
        // roomId を渡すと、バックエンドがそのルームのカテゴリに沿った
        // プロフィール項目を返す
        const profile = await getProfile(profileId, roomId || undefined)
        const eventType = toRoomType(profile.eventType)
        const photoUrl =
          eventType === 'business'
            ? profile.bizPhotoUrl
            : eventType === 'dating'
              ? profile.lovePhotoUrl
              : profile.careerPhotoUrl
        const name =
          eventType === 'business'
            ? profile.bizName
            : eventType === 'dating'
              ? profile.loveNickname
              : profile.careerName

        setPartner({
          id: profile.userId,
          // 主催者はプロフィール未登録の場合があるため既定の表示名に寄せる
          name: profile.isRoomOwner
            ? resolveOwnerName(name, profile.username)
            : name || profile.username || `User ${profile.userId}`,
          photoUrl,
        })
        setPartnerError(null)
      } catch (error) {
        console.error('Failed to fetch chat partner:', error)
        setPartner(null)
        const code = (error as { code?: string }).code
        setPartnerError(
          code === 'functions/permission-denied'
            ? 'この相手のプロフィールを閲覧する権限がありません。'
            : code === 'functions/not-found'
              ? '相手の情報が見つかりませんでした。'
              : '相手の情報の取得に失敗しました。時間をおいて再度お試しください。',
        )
      }

      try {
        const result = await getPublicState(profileId)
        setIsDisclosed(result.isPublic)
      } catch (error) {
        console.error('Failed to fetch public state:', error)
      }
    }

    fetchData()
  }, [profileId, roomId])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Effect 1: チャットID取得
  useEffect(() => {
    if (!profileId || !currentUserId || !roomId) return
    let cancelled = false
    setIsInitializing(true)
    const initializeChat = async () => {
      try {
        const result = await getOrCreateChat(roomId, profileId)
        if (cancelled) return

        // Firestoreのアクセス制御に使う参加者UIDを先に登録してから
        // メッセージの購読・送信を行う
        await ensureChatDocument(result.chatId, result.participantUids ?? [])
        if (cancelled) return

        setPartnerFirebaseUid(
          (result.participantUids ?? []).find((uid) => uid !== currentUserId) ?? null,
        )
        setActiveChatId(result.chatId)
      } catch (error) {
        console.error('Failed to initialize chat:', error)
      } finally {
        if (!cancelled) setIsInitializing(false)
      }
    }
    void initializeChat()
    return () => { cancelled = true }
  }, [profileId, currentUserId, roomId])

  // Effect 2: DBから履歴を取得して初期表示
  useEffect(() => {
    if (!activeChatId) return
    let cancelled = false
    setIsLoadingHistory(true)
    const loadHistory = async () => {
      try {
        const history = await getChatMessages(activeChatId)
        if (!cancelled) {
          setMessages(history)
        }
      } catch (error) {
        console.error('Failed to load chat history:', error)
      } finally {
        if (!cancelled) {
          setDbLoadedAt(new Date())
          setIsLoadingHistory(false)
        }
      }
    }
    void loadHistory()
    return () => { cancelled = true }
  }, [activeChatId])

  // Effect 3: チャットを開いたら未読通知クリア（Firestore）＋ 既読更新（DB）
  useEffect(() => {
    if (!activeChatId || !currentUserId) return
    clearChatNotification(activeChatId, currentUserId).catch(console.error)
    markChatAsRead(activeChatId).catch(console.error)
  }, [activeChatId, currentUserId])

  // Effect 5: DB取得後の新着メッセージをFirestoreでリアルタイム受信
  useEffect(() => {
    if (!activeChatId || !dbLoadedAt) return
    const unsubscribe = subscribeNewChatMessages(activeChatId, dbLoadedAt, (newMsgs) => {
      if (newMsgs.length > 0) {
        setMessages((prev) => {
          const existingIds = new Set(prev.map(m => m.id))
          const unique = newMsgs.filter(m => !existingIds.has(m.id))
          return [...prev, ...unique]
        })
      }
    })
    return () => unsubscribe()
  }, [activeChatId, dbLoadedAt])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !profileId || !activeChatId) return

    const content = newMessage.trim()
    setNewMessage('')
    setIsSending(true)
    try {
      // 権限チェックを伴うDB保存を先に行う。Firestoreを先に書くと、
      // DB保存に失敗しても画面上は送信できたように見えてしまうため。
      await sendChatMessage(activeChatId, content)
      // Firestoreへ書き込み（相手側へのリアルタイム表示）
      await sendChatMessageRealtime(activeChatId, currentUserId, content)
      // 相手に未読通知を送る（バックグラウンド）
      if (partnerFirebaseUid && currentDbUserId) {
        notifyPartnerOfNewMessage(activeChatId, partnerFirebaseUid, currentDbUserId, content).catch((err) => {
          console.error('Failed to notify partner:', err)
        })
      }
    } catch (error) {
      console.error('Failed to send message:', error)
      setNewMessage(content)
      showError('メッセージの送信に失敗しました。時間をおいて再度お試しください。')
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleBack = () => {
    navigate(-1)
  }

  const handleToggleRequest = (next: boolean) => {
    setPendingToggle(next)
    setConfirmOpen(true)
  }

  const handleDisclosureConfirm = async () => {
    if (!profileId) return
    
    setIsTogglingPublic(true)
    try {
      if (pendingToggle) {
        await setPublicState(profileId)
      } else {
        await removePublicState(profileId)
      }
      setIsDisclosed(pendingToggle)
      setConfirmOpen(false)
    } catch (error) {
      console.error('Failed to update public state:', error)
      const err = error as { code?: string; message?: string }
      showError(
        err.code === 'functions/permission-denied'
          ? '公開設定を変更する権限がありません。'
          : `公開設定の変更に失敗しました。${err.message ?? ''}`,
      )
    } finally {
      setIsTogglingPublic(false)
    }
  }

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // APIレスポンスをProfile型に変換（公開フラグに基づいて連絡先を設定）
  const mapResponseToProfile = (response: ProfileDetailResponse): Profile => {
    const base = {
      id: response.userId,
      pr: response.prText,
      photoUrl: undefined,
      createdAt: new Date(),
      subInfos: response.roomSubInfo,
    }

    const profileType = toRoomType(response.eventType)

    if (profileType === 'business') {
      return {
        ...base,
        type: 'business' as const,
        name: response.bizName || '',
        companyName: response.bizCompanyName || '',
        position: response.bizPosition || '',
        businessContent: response.bizContent || '',
        comment: response.bizComment || '',
        wantToMatch: response.bizTargetIndustry || '',
        notWantToMatch: response.bizExcludeIndustry || '',
        contactEmail: response.bizEmail || '',
        contactPhone: response.bizTel || '',
      }
    } else if (profileType === 'dating') {
      return {
        ...base,
        type: 'dating' as const,
        name: response.loveNickname || '',
        age: response.loveAge || 0,
        occupation: response.loveJob || '',
        gender: (response.loveGender || 'other') as 'male' | 'female' | 'other',
        annualIncome: response.loveAnnualIncome || '',
        wantChildren: response.loveWantsKids || undefined,
        wantToMatch: response.loveTargetPerson || '',
        notWantToMatch: response.loveExcludePerson || '',
        contactEmail: response.loveContactInfo || '',
        contactPhone: response.loveTel || '',
        userNumber: response.userId,
      }
    } else {
      return {
        ...base,
        type: 'job' as const,
        name: response.careerName || '',
        companyName: response.careerPrevCompany || '',
        lastPosition: response.careerLastPosition || '',
        education: response.careerEducationLevel || '',
        schoolName: response.careerSchoolName || '',
        age: response.careerAge || 0,
        gender: (response.careerGender || 'other') as 'male' | 'female' | 'other',
        comment: response.careerComment || '',
        wantToMatch: response.careerTargetCompany || '',
        notWantToMatch: response.careerExcludeConditions || '',
        contactEmail: response.careerEmail || '',
        contactPhone: response.careerTel || '',
      }
    }
  }

  const handleAvatarClick = async () => {
    if (!profileId) return

    setProfileDetailOpen(true)
    setIsLoadingProfile(true)

    try {
      const response = await getProfile(profileId, roomId)
      const mappedProfile = mapResponseToProfile(response)
      setProfileDetail(mappedProfile)
    } catch (error) {
      console.error('Failed to fetch profile detail:', error)
      setProfileDetail(null)
    } finally {
      setIsLoadingProfile(false)
    }
  }

  // URLパラメータ不足は本当のエラー
  if (!profileId || !roomId) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
        <Alert severity="error">チャット開始情報（roomId）が不足しています</Alert>
      </Box>
    )
  }

  // 相手情報は非同期で取得するため、取得できるまでは読み込み表示にする。
  // ここでエラー文言を出すと、チャットを開くたびに一瞬エラーが表示されてしまう。
  if (!partner) {
    // 取得に失敗した場合は読み込み表示のままにせず、原因と戻る導線を出す
    if (partnerError) {
      return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, px: 3 }}>
          <Alert severity="error" sx={{ maxWidth: 480, width: '100%', borderRadius: 2 }}>
            {partnerError}
          </Alert>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            戻る
          </Button>
        </Box>
      )
    }
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">チャットを準備中...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" color="primary">
        <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
          <IconButton edge="start" color="inherit" onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar
            src={partner.photoUrl}
            alt={partner.name}
            onClick={handleAvatarClick}
            sx={{ width: 36, height: 36, ml: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
          >
            {partner.name?.charAt(0)}
          </Avatar>
          <Typography variant="h6" sx={{ ml: 1, flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {partner.name}
          </Typography>

          {/* 公開トグル + ？ヘルプ */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'rgba(0,0,0,0.25)',
              borderRadius: 3,
              px: 1,
              py: 0.25,
              ml: 1,
            }}
          >
            {isDisclosed
              ? <LockOpenIcon sx={{ fontSize: 16, color: '#86efac', mr: 0.5 }} />
              : <LockIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)', mr: 0.5 }} />
            }
            <FormControlLabel
              control={
                <Switch
                  checked={isDisclosed}
                  onChange={(e) => handleToggleRequest(e.target.checked)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#86efac' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#4ade80' },
                  }}
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{
                    color: isDisclosed ? '#86efac' : 'rgba(255,255,255,0.7)',
                    fontWeight: 700,
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isDisclosed ? '公開中' : '非公開'}
                </Typography>
              }
              labelPlacement="end"
              sx={{ m: 0 }}
            />
            <IconButton
              color="inherit"
              onClick={() => setHelpOpen(true)}
              size="small"
              sx={{ p: 0.25, opacity: 0.7, ml: 0.25 }}
            >
              <HelpOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* 公開切り替え確認モーダル */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700, textAlign: 'center' }}>
          {pendingToggle
            ? <LockOpenIcon sx={{ color: 'primary.main', fontSize: 36, display: 'block', mx: 'auto', mb: 1 }} />
            : <LockIcon sx={{ color: 'text.secondary', fontSize: 36, display: 'block', mx: 'auto', mb: 1 }} />
          }
          {pendingToggle ? 'プロフィールを公開しますか？' : '公開を取り消しますか？'}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          {pendingToggle ? (
            <Typography variant="body2" color="text.secondary">
              <strong>{partner.name}</strong>さんにだけ、あなたの非公開情報（連絡先など）を開示します。
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              <strong>{partner.name}</strong>さんへの非公開情報の開示を取り消します。
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleDisclosureConfirm}
            color={pendingToggle ? 'primary' : 'inherit'}
            disabled={isTogglingPublic}
            startIcon={isTogglingPublic ? <CircularProgress size={18} color="inherit" /> : undefined}
            sx={{ borderRadius: 2, fontWeight: 700, py: 1.2 }}
          >
            {isTogglingPublic ? '処理中...' : pendingToggle ? '公開する' : '取り消す'}
          </Button>
          <Button
            variant="text"
            fullWidth
            color="inherit"
            onClick={() => setConfirmOpen(false)}
            disabled={isTogglingPublic}
            sx={{ borderRadius: 2, color: 'text.secondary' }}
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      {/* ヘルプ説明モーダル */}
      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpOutlineIcon sx={{ color: 'primary.main' }} />
          公開機能とは？
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            「公開する」ボタンは、<strong>この相手にだけ</strong>あなたの非公開情報（連絡先・メールアドレスなど）を開示する機能です。
          </Typography>
          <Typography variant="body2" color="text.secondary">
            他の参加者には一切公開されません。お互いに信頼関係が築けた相手に、安心して連絡先を渡せます。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setHelpOpen(false)}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* プロフィール詳細モーダル */}
      <Dialog
        open={profileDetailOpen}
        onClose={() => setProfileDetailOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography fontWeight={700}>プロフィール詳細</Typography>
          <IconButton size="small" onClick={() => setProfileDetailOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {isLoadingProfile && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!isLoadingProfile && profileDetail && (
            <ProfileDetailView profile={profileDetail} />
          )}
          {!isLoadingProfile && !profileDetail && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                プロフィールの取得に失敗しました
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setProfileDetailOpen(false)}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          bgcolor: '#f5f5f5',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {(isInitializing || isLoadingHistory) && (
          <Box sx={{ textAlign: 'center', py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={32} />
            <Typography color="text.secondary">
              {isInitializing ? 'チャットを準備中...' : '履歴を読み込み中...'}
            </Typography>
          </Box>
        )}
        {!isInitializing && !isLoadingHistory && messages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {partner.name}さんとのチャットを開始しましょう
            </Typography>
          </Box>
        )}
        {messages.map((message) => {
          const isOwn =
            message.senderId === currentUserId ||      // Firestoreメッセージ（Firebase UID）
            message.senderId === currentDbUserId       // DBメッセージ（DB user ID）
          return (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: isOwn ? 'flex-end' : 'flex-start',
                mb: 1,
              }}
            >
              <Paper
                sx={{
                  p: 1.5,
                  maxWidth: '70%',
                  bgcolor: isOwn ? 'primary.main' : 'white',
                  color: isOwn ? 'white' : 'text.primary',
                  borderRadius: isOwn
                    ? '16px 16px 4px 16px'
                    : '16px 16px 16px 4px',
                }}
              >
                <Typography variant="body1">{message.content}</Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'right',
                    mt: 0.5,
                    opacity: 0.7,
                  }}
                >
                  {formatTime(message.createdAt)}
                </Typography>
              </Paper>
            </Box>
          )
        })}
        <div ref={messagesEndRef} />
      </Box>

      <Paper
        sx={{
          p: 1,
          display: 'flex',
          alignItems: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="メッセージを入力..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          size="small"
          sx={{ mr: 1 }}
        />
        <IconButton
          color="primary"
          onClick={() => void handleSendMessage()}
          disabled={!newMessage.trim() || !activeChatId || isSending}
        >
          {isSending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
        </IconButton>
      </Paper>
    </Box>
  )
}
